/**
 * HitlReviewQueue — Universal Applicability Engine HITL inbox.
 *
 * Shown above the BEP Rules Review list when at least one rule has been
 * routed to ``HITL_REVIEW`` status by the Applicability Engine.
 *
 * Each entry exposes:
 *   • the rule's natural-language description + source page
 *   • the engine's best guess (applicability_type + entities) and
 *     confidence + reason for routing to HITL
 *   • a "Promote to active" action that lets the reviewer pin the
 *     correct ifc_entities + discipline and accept the rule
 *
 * Promotion calls ``POST /api/projects/{id}/rules/hitl/{rule_id}/promote``
 * with the reviewer-pinned scope.  On success the entry vanishes from
 * this list and (when ``source === 'extracted'``) flows into
 * ``confirmed_rules`` as an active rule. The parent page is told via
 * ``onPromoted`` so it can refresh its candidate / confirmed rule
 * counts.
 *
 * Backfill: ``POST /api/projects/{id}/rules/applicability/backfill``
 * is invoked when the reviewer clicks "Re-run applicability analysis".
 * Idempotent — only re-infers missing entries unless force=true.
 */
import { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { AlertCircle, Loader2, RefreshCw, Check, Wand2, X } from 'lucide-react';
import { apiFetch } from '../lib/supabase';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const queueKey = item => item.candidate_pk || item.candidate_id || item.rule_id;

const ENTITY_PRESETS = [
  { value: 'WALL',          label: 'Walls' },
  { value: 'DOOR',          label: 'Doors' },
  { value: 'WINDOW',        label: 'Windows' },
  { value: 'SLAB',          label: 'Slabs' },
  { value: 'ROOF',          label: 'Roofs' },
  { value: 'STAIR',         label: 'Stairs' },
  { value: 'COVERING',      label: 'Coverings' },
  { value: 'CURTAIN_WALL',  label: 'Curtain walls' },
  { value: 'SPACE',         label: 'Spaces' },
  { value: 'BEAM',          label: 'Beams' },
  { value: 'COLUMN',        label: 'Columns' },
  { value: 'FOOTING',       label: 'Footings' },
  { value: 'MEP_EQUIPMENT', label: 'MEP equipment' },
  { value: 'MEP_DISTRIBUTION', label: 'MEP distribution' },
  { value: 'MEP_TERMINAL',  label: 'MEP terminals' },
  { value: 'ANY_ELEMENT',   label: 'All elements (model-wide)' },
];

const GROUP_ORDER = [
  { key: 'conflict',             label: 'Conflicts',
    hint: 'contradicting values for the same rule — pick the correct one' },
  { key: 'verifier_review',      label: 'Verifier review',
    hint: 'Sol marked these needs_review — confirm or dismiss' },
  { key: 'readiness_hold',       label: 'Readiness holds',
    hint: 'missing facet-required fields — not promotable as-is' },
  { key: 'legacy_hitl',          label: 'Legacy HITL',
    hint: 'routed to HITL before the facet-readiness gate existed' },
  { key: 'applicability_review', label: 'Applicability review',
    hint: 'rule is sound but its scope needs pinning' },
];

export default function HitlReviewQueue({ projectId, onPromoted, onQueueChange }) {
  const [items, setItems]       = useState([]);
  const [summary, setSummary]   = useState({});
  const [groups, setGroups]     = useState(null);
  const [groupCounts, setGroupCounts] = useState(null);
  const [loading, setLoading]   = useState(false);
  const [backfilling, setBackfilling] = useState(false);
  const [pickerOpen, setPickerOpen]   = useState(null);   // rule_id of open picker
  // { rule_id → { ifc_entities: string[], discipline: string } }
  const [pinned, setPinned]     = useState({});
  const [promoting, setPromoting] = useState(null);       // rule_id mid-promote
  const [dismissing, setDismissing] = useState(null);     // rule_id mid-dismiss

  const fetchQueue = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const resp = await apiFetch(`${API_URL}/api/projects/${projectId}/rules/hitl`);
      if (!resp.ok) throw new Error(`Failed (${resp.status})`);
      const json = await resp.json();
      setItems(json.hitl_rules || []);
      setGroups(json.groups || null);
      setGroupCounts(json.group_counts || null);
      setSummary(json.applicability_summary || {});
      const n = (json.hitl_rules || []).length;
      queueMicrotask(() => { if (typeof onQueueChange === 'function') onQueueChange(n); });
    } catch (e) {
      toast.error('Could not load HITL queue', { description: e.message || '' });
    } finally {
      setLoading(false);
    }
  }, [projectId, onQueueChange]);

  useEffect(() => { fetchQueue(); }, [fetchQueue]);

  const runBackfill = async () => {
    setBackfilling(true);
    try {
      const resp = await apiFetch(
        `${API_URL}/api/projects/${projectId}/rules/applicability/backfill?force=false`,
        { method: 'POST' },
      );
      if (!resp.ok) throw new Error(`Failed (${resp.status})`);
      const json = await resp.json();
      toast.success(
        `Backfill ran: ${json.rules_resolved} resolved, ${json.rules_routed_to_hitl} HITL, ${json.rules_skipped_existing} unchanged`
      );
      await fetchQueue();
    } catch (e) {
      toast.error('Backfill failed', { description: e.message || '' });
    } finally {
      setBackfilling(false);
    }
  };

  const togglePinnedEntity = (ruleId, value) => {
    setPinned(prev => {
      const cur = prev[ruleId] || { ifc_entities: [], discipline: '' };
      const has = cur.ifc_entities.includes(value);
      return {
        ...prev,
        [ruleId]: {
          ...cur,
          ifc_entities: has
            ? cur.ifc_entities.filter(e => e !== value)
            : [...cur.ifc_entities, value],
        },
      };
    });
  };

  const setPinnedDiscipline = (ruleId, value) => {
    setPinned(prev => ({
      ...prev,
      [ruleId]: { ...(prev[ruleId] || { ifc_entities: [] }), discipline: value },
    }));
  };

  const promote = async (item) => {
    const ruleId = item.rule_id;
    const p = pinned[queueKey(item)] || {};
    // Default: accept engine's current best guess.
    const body = {
      candidate_pk: item.candidate_pk || item.candidate_id || undefined,
      target_ifc_classes: (p.ifc_entities || []).filter(value => value.startsWith('Ifc')),
      ifc_entities: p.ifc_entities && p.ifc_entities.length > 0
        ? p.ifc_entities
        : (item.ifc_entities && item.ifc_entities.length > 0 ? item.ifc_entities : null),
      discipline: p.discipline || item.discipline || null,
    };
    if (!body.candidate_pk && !body.ifc_entities && !body.discipline) {
      toast.warning('Pin at least one IFC entity or a discipline before promoting.');
      return;
    }
    setPromoting(ruleId);
    try {
      const resp = await apiFetch(
        `${API_URL}/api/projects/${projectId}/rules/hitl/${encodeURIComponent(ruleId)}/promote`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        },
      );
      if (!resp.ok) {
        const t = await resp.text().catch(() => '');
        throw new Error(`Failed (${resp.status}): ${t.slice(0, 200)}`);
      }
      const result = await resp.json();
      if (result.status === 'blocked' || result.status === 'requires_scope') {
        toast.warning(`${item.review_ref || ruleId}: ${result.reason}`);
        return;
      }
      if (result.status === 'selected_engine_pending') {
        toast.message(`${item.review_ref || ruleId}: User selected · Federation/engine support pending`);
      } else {
        toast.success(`${item.review_ref || ruleId}: Runtime active`);
      }
      setItems(prev => {
        const next = result.status === 'selected_engine_pending'
          ? prev.map(i => queueKey(i) === queueKey(item) ? {...i, user_selected: true, human_decision: 'deferred'} : i)
          : prev.filter(i => queueKey(i) !== queueKey(item));
        queueMicrotask(() => { if (typeof onQueueChange === 'function') onQueueChange(next.length); });
        return next;
      });
      setPickerOpen(null);
      if (result.status !== 'selected_engine_pending' && typeof onPromoted === 'function') onPromoted(ruleId);
    } catch (e) {
      toast.error('Promotion failed', { description: e.message || '' });
    } finally {
      setPromoting(null);
    }
  };

  const dismiss = async (item) => {
    const ruleId = item.rule_id;
    setDismissing(ruleId);
    try {
      const resp = await apiFetch(
        `${API_URL}/api/projects/${projectId}/rules/hitl/${encodeURIComponent(ruleId)}/dismiss`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ reviewer_note: 'Dismissed from HITL queue UI' }),
        },
      );
      if (!resp.ok) {
        const t = await resp.text().catch(() => '');
        throw new Error(`Failed (${resp.status}): ${t.slice(0, 200)}`);
      }
      toast.success(`Rule "${item.rule_name || ruleId}" dismissed.`);
      setItems(prev => {
        const next = prev.filter(i => i.rule_id !== ruleId);
        queueMicrotask(() => { if (typeof onQueueChange === 'function') onQueueChange(next.length); });
        return next;
      });
      if (pickerOpen === ruleId) setPickerOpen(null);
    } catch (e) {
      toast.error('Dismiss failed', { description: e.message || '' });
    } finally {
      setDismissing(null);
    }
  };

  if (!loading && items.length === 0 && (summary.hitl || 0) === 0) {
    return null;  // clean state — no chip surface
  }

  return (
    <Card className="border-[hsl(var(--warning))]/40 bg-[hsl(var(--warning))]/5"
          data-testid="hitl-review-queue">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <CardTitle className="flex items-center gap-2 text-base">
              <AlertCircle className="h-4 w-4 text-[hsl(var(--warning))]" />
              Applicability HITL Review
              <Badge variant="outline" className="text-[10px] px-1.5"
                     data-testid="hitl-count-badge">
                {items.length} pending
              </Badge>
            </CardTitle>
            <CardDescription className="text-xs">
              Rules where the Universal Applicability Engine could not confidently
              pin the IFC scope. Pin entities or discipline, then promote.
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={fetchQueue}
                    disabled={loading} data-testid="hitl-refresh-btn">
              <RefreshCw className={`h-3.5 w-3.5 mr-1 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button size="sm" variant="outline" onClick={runBackfill}
                    disabled={backfilling} data-testid="hitl-backfill-btn"
                    title="Re-run the deterministic applicability inference across this project's confirmed rules. Idempotent — only re-infers missing entries.">
              {backfilling ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> :
                              <Wand2 className="h-3.5 w-3.5 mr-1" />}
              Re-run inference
            </Button>
          </div>
        </div>
        {groupCounts && (
          <p className="text-[11px] text-muted-foreground mt-1" data-testid="hitl-summary">
            {items.length} open — conflicts {groupCounts.conflict ?? 0} ·
            verifier review {groupCounts.verifier_review ?? 0} ·
            readiness holds {groupCounts.readiness_hold ?? 0} ·
            legacy HITL {groupCounts.legacy_hitl ?? 0} ·
            applicability {groupCounts.applicability_review ?? 0}
            {typeof groupCounts.promoted === 'number' &&
              ` — plus ${groupCounts.promoted} promoted, ${groupCounts.rejected_duplicate ?? 0} duplicates (not reviewable)`}
          </p>
        )}
        {summary && (summary.total != null) && (
          <p className="text-[11px] text-muted-foreground mt-0.5" data-testid="hitl-applicability-summary">
            Applicability engine (promoted rules only): {summary.resolved || 0} resolved ·
            {' '}{summary.hitl || 0} HITL · avg confidence {Math.round((summary.avg_confidence || 0) * 100)}%
          </p>
        )}
      </CardHeader>
      <CardContent className="space-y-2 max-h-[440px] overflow-y-auto pr-1">
        {items.length === 0 && !loading && (
          <p className="text-xs text-muted-foreground text-center py-2">
            HITL queue is empty.
          </p>
        )}
        {GROUP_ORDER.map(({ key, label, hint }) => {
          const list = (groups && groups[key]) ||
                       (key === 'legacy_hitl' && !groups ? items : []);
          if (!list || list.length === 0) return null;
          return (
            <div key={key} data-testid={`hitl-group-${key}`}>
              <div className="flex items-center gap-2 mt-3 mb-1.5">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-foreground">
                  {label}
                </p>
                <Badge variant="outline" className="text-[10px] px-1.5"
                       data-testid={`hitl-group-count-${key}`}>
                  {list.length}
                </Badge>
                <span className="text-[10px] text-muted-foreground">{hint}</span>
              </div>
              <div className="space-y-2">
                {list.map((item) => {
          const identity = queueKey(item);
          const isOpen = pickerOpen === identity;
          const p = pinned[identity] || { ifc_entities: [], discipline: '' };
          const entityPresets = item.candidate_pk ? (item.scope_options || []).map(value => ({ value, label: value })) : ENTITY_PRESETS;
          const isConflict = !!item.is_conflict;
          return (
            <div
              key={identity}
              className={`border rounded p-3 bg-background ${
                isConflict
                  ? 'border-destructive/60 ring-1 ring-destructive/30'
                  : 'border-[hsl(var(--warning))]/30'
              }`}
              data-testid={`hitl-row-${identity}`}
            >
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span
                      className="inline-flex items-center justify-center min-w-[1.75rem] h-5 px-1.5 rounded-md text-[10px] font-semibold tabular-nums bg-muted text-muted-foreground shrink-0"
                      data-testid={`hitl-review-ref-${identity}`}
                      title={item.rule_id}
                    >
                      {item.review_ref || item.rule_id}
                    </span>
                    {isConflict && (
                      <Badge
                        className="text-[10px] px-1.5 bg-destructive text-destructive-foreground"
                        data-testid={`hitl-conflict-badge-${identity}`}
                      >
                        CONFLICT
                      </Badge>
                    )}
                    {item.applicability_type && (
                      <Badge variant="outline" className="text-[10px] px-1.5">
                        {item.applicability_type}
                      </Badge>
                    )}
                    <Badge variant="outline" className="text-[10px] px-1.5">
                      {item.source}
                    </Badge>
                    {item.check_type && (
                      <Badge variant="outline" className="text-[10px] px-1.5 font-mono">
                        {item.check_type}
                      </Badge>
                    )}
                    <span className="text-[10px] text-muted-foreground">
                      confidence {typeof item.confidence === 'number'
                        ? `${Math.round(item.confidence * 100)}%` : '—'}
                    </span>
                  </div>
                  <p className="text-sm text-foreground" data-testid={`hitl-desc-${identity}`}>
                    {item.description || item.rule_name || item.rule_id}
                  </p>
                  {isConflict && item.conflict_statement && (
                    <p
                      className="text-[11px] text-destructive mt-1 font-medium"
                      data-testid={`hitl-conflict-statement-${identity}`}
                    >
                      <span className="uppercase tracking-wide">Contradiction:</span>{' '}
                      {item.conflict_statement}
                    </p>
                  )}
                  {item.reason && (
                    <p className="text-[11px] text-muted-foreground mt-1">
                      <span className="text-[hsl(var(--warning))]">Why HITL:</span> {item.reason}
                    </p>
                  )}
                  {item.user_selected && item.runtime_support === 'engine_pending' && <p className="text-xs mt-1" data-testid={`hitl-engine-pending-${identity}`}>
                    User selected · Engine support: pending · {item.runtime_reason}
                  </p>}
                  {item.source_text && (
                    <p className="text-[11px] text-muted-foreground mt-0.5 italic truncate">
                      "{item.source_text}"
                    </p>
                  )}
                </div>
                {(item.human_selectable === true || (!item.candidate_pk && item.group !== 'readiness_hold')) && (
                <div className="flex items-center gap-1.5">
                  <Button
                    size="sm"
                    variant={isOpen ? 'ghost' : 'outline'}
                    onClick={() => setPickerOpen(isOpen ? null : identity)}
                    data-testid={`hitl-pin-btn-${identity}`}
                  >
                    {isOpen ? <X className="h-3.5 w-3.5" /> : 'Review selection'}
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-muted-foreground hover:text-destructive"
                    onClick={() => dismiss(item)}
                    disabled={dismissing === item.rule_id}
                    title="Dismiss this rule — it will not become an active rule."
                    data-testid={`hitl-dismiss-btn-${identity}`}
                  >
                    {dismissing === item.rule_id
                      ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      : 'Dismiss'}
                  </Button>
                </div>
                )}
              </div>

              {isOpen && (
                <div className="mt-3 pt-3 border-t border-border space-y-2"
                     data-testid={`hitl-picker-${identity}`}>
                  <p className="text-xs" data-testid={`hitl-runtime-${identity}`}>{item.runtime_reason}</p>
                  <div>
                    <p className="text-[11px] text-muted-foreground mb-1">
                      Pin IFC entities (toggle one or more)
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                      {entityPresets.map((preset) => {
                        const active = p.ifc_entities.includes(preset.value);
                        return (
                          <button
                            key={preset.value}
                            onClick={() => togglePinnedEntity(identity, preset.value)}
                            data-testid={`hitl-entity-btn-${identity}-${preset.value}`}
                            className={`text-[11px] px-2 py-1 rounded border transition-colors ${
                              active
                                ? 'bg-accent/15 text-accent border-accent/40'
                                : 'bg-muted/40 text-muted-foreground border-transparent hover:border-border'
                            }`}
                          >
                            {preset.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {!item.candidate_pk && <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-[11px] text-muted-foreground">
                      …or pin a discipline:
                    </p>
                    {['Architecture', 'Structure', 'MEP', 'Infrastructure'].map((d) => (
                      <button
                        key={d}
                        onClick={() => setPinnedDiscipline(identity, p.discipline === d ? '' : d)}
                        data-testid={`hitl-discipline-btn-${identity}-${d}`}
                        className={`text-[11px] px-2 py-1 rounded border transition-colors ${
                          p.discipline === d
                            ? 'bg-accent/15 text-accent border-accent/40'
                            : 'bg-muted/40 text-muted-foreground border-transparent hover:border-border'
                        }`}
                      >
                        {d}
                      </button>
                    ))}
                  </div>}

                  <Button
                    size="sm"
                    onClick={() => promote(item)}
                    disabled={promoting === item.rule_id || item.human_selectable === false}
                    data-testid={`hitl-promote-btn-${identity}`}
                    className="bg-[hsl(var(--success))] hover:bg-[hsl(var(--success))]/90 text-white"
                  >
                    {promoting === item.rule_id ? (
                      <><Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> Promoting…</>
                    ) : (
                      <><Check className="h-3.5 w-3.5 mr-1" /> {item.runtime_support === 'engine_pending' ? 'Save Engine-Pending Selection' : 'Confirm Requirement'}</>
                    )}
                  </Button>
                </div>
              )}
            </div>
          );
                })}
              </div>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}
